import * as React from 'react';
import { AlertMessage, AlertType, CustomButton, ButtonType, SpinnerWrapper } from "soprasteria-ui-library";
import { useAuth } from '../../../../App';
import { ReferralTreatmentReportingApi } from '../../../../api/ReferralTreatmentReportingApi';
import { ReferralTreatmentReportingTable } from './ReferralTreatmentReportingTable';
import { IReferralTreatment, IReferralTreatmentCode, IReferral, IReferralAssessment } from '../../../../models/IReferral';
import { ReferralTreatmentModal } from './ReferralTreatmentModal';
import { setMessage } from '../../../../helpers/MessageHelper';
import { ReferralAssessmentApi } from '../../../../api/ReferralAssessmentApi';

interface IReferralTreatmentReporting {
    referral?: IReferral;
    getReferral: (referralId: number) => void;
    gotReportedTreatments?: (val: boolean) => void;
}

export const ReferralTreatmentReporting: React.FunctionComponent<IReferralTreatmentReporting> = ({ referral, getReferral, gotReportedTreatments }) => {

    const auth = useAuth();
    const treatmentReportApi = new ReferralTreatmentReportingApi(auth);
    const treatmentAssessApi = new ReferralAssessmentApi(auth);

    const [referralTreatments, setReferralTreatments] = React.useState<IReferralTreatment[]>();
    const [isApiFetching, setIsApiFetching] = React.useState<boolean>(false);
    const [referralTreatmentCodes, setReferralTreatmentCodes] = React.useState<IReferralTreatmentCode[]>();
    const [successMessage, setSuccessMessage] = React.useState<string>();
    const [renderTreatmentsModal, setRenderTreatmentsModal] = React.useState<boolean>(false);
    const [multipleTreatments, setMultipleTreatments] = React.useState<boolean>(false);

    React.useEffect(() => {
        loadReferralTreatments();
        loadReferralTreatmentCodes();
    }, []);

    const loadReferralTreatmentCodes = () => {
        if (referral) {
            treatmentReportApi.getReferralTreatmentCodes(referral.referralId ?? 0, (treatmentCodes) => {
                setReferralTreatmentCodes(treatmentCodes);
            });
        }
    }

    const loadReferralTreatments = () => {
        if (referral?.referralId) {
            setIsApiFetching(true);
            treatmentReportApi.getReferralTreatments(referral.referralId.toString(), (treatments) => {
                setReferralTreatments(treatments);
                if (gotReportedTreatments) {
                    gotReportedTreatments(treatments.length > 0 ? true : false);
                }
            }, () => {
                if (gotReportedTreatments) {
                    gotReportedTreatments(false);
                }
            }, () => {
                setIsApiFetching(false);
            });
        }
    }

    const onTreatmentSubmitSuccess = (reopenTreatmentsModal: boolean) => {
        if (referral?.referralId) {
            const message = setMessage("Åtgärdskoden har lagts till", successMessage ?? "");
            setSuccessMessage(message);
            loadReferralTreatments();
            getReferral(referral.referralId);
            setRenderTreatmentsModal(reopenTreatmentsModal);
        }
    }

    const onCloseTreatmentModal = () => {
        setMultipleTreatments(multipleTreatments);
        document.getElementById("close-add-referral-treatment-modal")?.click();
        setRenderTreatmentsModal(false);
    }

    const deleteReferralTreatment = (patientTreatmentId: number) => {
        if (referral?.referralId) {
            treatmentReportApi.deleteReferralTreatment(patientTreatmentId.toString(), () => {
                const message = setMessage("Åtgärdskoden har tagits bort", successMessage ?? "");
                setSuccessMessage(message);
                loadReferralTreatments();
                getReferral(referral.referralId ?? 0);
            });
        }
    }

    const onTreatmentCredit = (patientTreatmentId: number) => {
        if (referral?.referralId) {
            treatmentReportApi.creditTreatment(patientTreatmentId, () => {
                const newMessage = setMessage("Åtgärdskoden inväntar kreditering", successMessage ?? "");
                setSuccessMessage(newMessage);
                loadReferralTreatments();
                getReferral(referral.referralId ?? 0);
            });
        }
    }

    const acceptTreatment = (referralTreatmentId: number) => {
        if (referral?.referralId && referral.canAssessTreatments) {
            treatmentAssessApi.acceptReferralTreatment(referral.referralId, referralTreatmentId, () => {
                const message = setMessage("Åtgärdskoden har godkänts", successMessage ?? "");
                setSuccessMessage(message);
                loadReferralTreatments();

            });
        }
    }

    const rejectTreatment = (treatmentId: number, comment: string) => {
        if (referral?.referralId && referral.canAssessTreatments) {
            const data: IReferralAssessment = {
                comment: comment
            }
            treatmentAssessApi.rejectReferralTreatment(referral.referralId, treatmentId, data, () => {
                const message = setMessage("Åtgärdskoden har avvisats", successMessage ?? "");
                setSuccessMessage(message);
                loadReferralTreatments();

            })
        }
    }

    return (
        <SpinnerWrapper isLoading={isApiFetching}>
            <div className="row">
                <h5 className="col-12">Åtgärdskoder</h5>
            </div>
            <AlertMessage alertType={AlertType.success} duration={3000}>{successMessage}</AlertMessage>
            <div className="panel text-right">
                {
                    referral?.canReportTreatments ?
                        <CustomButton buttonText="Lägg till åtgärdskod" buttonType={ButtonType.create} onClick={() => { setMultipleTreatments(false); setRenderTreatmentsModal(true)}} />
                        : null
                }
            </div>
            <div className="row">
                {
                    referralTreatments ?
                        <div className="col-12">
                            <ReferralTreatmentReportingTable referralTreatments={referralTreatments} onDelete={deleteReferralTreatment} isEditable={referral?.canReportTreatments} treatmentsTotalPrice={referral?.totalTreatmentPrice} canAssessTreatments={referral?.canAssessTreatments} acceptTreatment={acceptTreatment} rejectTreatment={rejectTreatment} onTreatmentCredit={onTreatmentCredit}/>
                        </div>
                        : null
                }
            </div>
            {
                referral?.canReportTreatments ?
                    <ReferralTreatmentModal
                        referralId={referral.referralId}
                        specialityId={referral.speciality?.specialityId ?? 0 }
                        renderModal={renderTreatmentsModal}
                        registerMultipleTreatments={multipleTreatments}
                        setRegisterMultipleTreatments={setMultipleTreatments}
                        onSubmitSuccess={onTreatmentSubmitSuccess}
                        onClose={onCloseTreatmentModal}
                        treatmentCodes={referralTreatmentCodes} 
                        recievedDate={referral.dateReceived} 
                        referralClosedDate={referral.closedDate} />
                    : null
            }
        </SpinnerWrapper>
    )
}